// App.js
import React, { useState } from 'react';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'Failed to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-gray-900 py-3 flex-shrink-0">
        <div className="max-w-5xl mx-auto text-center px-6">
          <h1 className="text-2xl font-bold text-white">BRANDON DIESBOURG</h1>
          <p className="text-gray-300 font-light tracking-wide text-xs">CRIMINAL DEFENCE LAWYER</p>
        </div>
      </header>

      <main className="flex-grow py-12 px-6">
        {/* About Section */}
        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden mb-10">
          <div className="bg-gray-800 py-5 px-8">
            <h2 className="text-xl font-light text-white text-center tracking-wide">ABOUT BRANDON</h2>
          </div>
          
          <div className="p-8">
            <p className="text-gray-700 leading-relaxed mb-6">
              Brandon E. Diesbourg is a lawyer licensed in the province of Ontario. He graduated from the University of Windsor Faculty of Law class of 2024 and practices in the Windsor, Ontario area with a focus on criminal law.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Brandon's interest in criminal law was sparked by his experiences volunteering with the Victim Witness Assistants Program. Seeing how the criminal justice system affects those impacted inspired him to become a passionate advocate for his clients' interests and legal rights.
            </p>
            <p className="text-gray-700 leading-relaxed">
              With a commitment to justice and a client-centered approach, Brandon works diligently to reach fair and equitable resolutions in his clients' favor. His in-depth understanding of the law combined with his compassionate nature makes him a dedicated representative for those navigating the criminal justice system.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden mb-12">
          <div className="bg-gray-800 py-5 px-8">
            <h2 className="text-xl font-light text-white text-center tracking-wide">CONTACT US</h2>
          </div>
          
          <div className="p-8">
            {submitSuccess ? (
              <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                <p>Thank you for contacting us. We will get back to you soon.</p>
              </div>
            ) : null}
            
            {submitError ? (
              <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <p>{submitError}</p>
              </div>
            ) : null}
            
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="mb-5">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="mb-5">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Your Situation</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={isSubmitting}
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className="w-full bg-gray-700 text-white py-3 px-4 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors text-lg font-light tracking-wide"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'SENDING...' : 'REQUEST CONSULTATION'}
              </button>
            </form>
          </div>
        </div>
      </main>

      <footer className="bg-gray-800 text-white py-1 text-center">
        <p className="text-xs">© 2025 Brandon Diesbourg. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;